import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL || '/api';  // Use nginx proxy path as fallback
const FASTAPI_URL = import.meta.env.VITE_FASTAPI_URL || '/fastapi';  // Use nginx proxy path as fallback

// Function to get the auth token from localStorage
const getToken = () => {
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;
  
  try {
    const user = JSON.parse(userJson);
    return user.token;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

// Generic API request function with authentication
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = getToken();
  
  if (!token) {
    throw new Error('Authentication required. Please log in.');
  }
  
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle authentication errors
      if (response.status === 401) {
        // Clear local storage and throw auth error
        localStorage.removeItem('user');
        throw new Error('Authentication expired. Please log in again.');
      }
      
      throw new Error(errorData.error || 'API request failed');
    }

    return response.json();
  } catch (error) {
    Sentry.captureException(error, { tags: { endpoint }, level: 'error' });
    console.error(`API request error for ${endpoint}:`, error);
    throw error;
  }
};


// Fetch documents API
export const fetchDocuments = async () => {
  return apiRequest('/documents', {
    method: 'GET',
  });
};

// Hook for fetching documents
export const useFetchDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDocs = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchDocuments();
      setDocuments(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return { documents, fetchDocs, isLoading, error };
};

// Function to update document type in MongoDB
export const updateDocumentType = async (documentId: string, documentType: string) => {
  if (!documentId) {
    console.error('Document ID is required for updating document type');
    throw new Error('Document ID is required');
  }
  
  try {
    return await apiRequest(`/documents/${documentId}`, { // Using PATCH to the document ID
      method: 'PATCH',
      body: JSON.stringify({ documentType: documentType }),
    });
  } catch (error) {
    console.error(`Error updating document type for document ${documentId}:`, error);
    throw error;
  }
};

// FastAPI Document Processing Functions

// Upload document to FastAPI server
export const uploadDocumentToFastAPI = async (file: File, document_type: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', document_type);
  
  try {
    const response = await fetch(`${FASTAPI_URL}/process`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to upload document to processing server');
    }
    
    return await response.json();
  } catch (error) {
    // Check if the error is due to the server not running
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Processing server is not running. Please start the FastAPI server.');
    }
    Sentry.captureException(error, { tags: { flow: 'document-processing', stage: 'fastapi-upload' } });
    console.error('Error uploading document to FastAPI:', error);
    throw error;
  }
};

// Update document processing results in MongoDB
export const updateDocumentProcessingResults = async (documentId: string, data: { status: string, jobId?: string, result?: any, warnings?: string[] }) => {
  if (!documentId) {
    console.error('Document ID is required for updating processing results');
    throw new Error('Document ID is required');
  }
  // Reverted to use apiRequest, which targets API_URL (main backend)
  try {
    return await apiRequest(`/documents/${documentId}/processing-results`, {
      method: 'POST', // Assuming main backend endpoint is POST as per original structure
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error(`Error updating document processing results for document ${documentId}:`, error);
    throw error;
  }
};

// Get document processing results from MongoDB
export const getDocumentProcessingResults = async (documentId: string) => {
  // Reverted to use apiRequest, which targets API_URL (main backend)
  try {
    return apiRequest(`/documents/${documentId}/processing-results`, {
      method: 'GET',
    });
  } catch (error) {
    console.error('Error getting document processing results:', error);
    throw error;
  }
};

// Check job status
export const checkJobStatus = async (jobId: string) => {
  try {
    const response = await fetch(`${FASTAPI_URL}/status/${jobId}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to check job status');
    }
    
    return await response.json();
  } catch (error) {
    // Check if the error is due to the server not running
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Processing server is not running. Please start the FastAPI server.');
    }
    Sentry.captureException(error, { tags: { flow: 'document-processing', stage: 'poll-status' } });
    console.error('Error checking job status:', error);
    throw error;
  }
};

// Get job result
export const getJobResult = async (jobId: string) => {
  try {
    const response = await fetch(`${FASTAPI_URL}/result/${jobId}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get job result');
    }
    
    return await response.json();
  } catch (error) {
    // Check if the error is due to the server not running
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Processing server is not running. Please start the FastAPI server.');
    }
    Sentry.captureException(error, { tags: { flow: 'document-processing', stage: 'fetch-result' } });
    console.error('Error getting job result:', error);
    throw error;
  }
};

// Hook for FastAPI document processing
export const useFastAPIProcessing = () => {
  const [status, setStatus] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const processDocumentWithFastAPI = async (file: File, document_type: string) => {
    setIsLoading(true);
    setError(null);
    setStatus('uploading');
    setResult(null);
    
    try {
      // Stop any existing polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
      
      // Upload document to FastAPI
      const uploadResponse = await uploadDocumentToFastAPI(file, document_type);
      const newJobId = uploadResponse.job_id;
      setJobId(newJobId);
      setStatus(uploadResponse.status);
      
      // Start polling for status updates
      const interval = setInterval(async () => {
        try {
          const statusResponse = await checkJobStatus(newJobId);
          setStatus(statusResponse.status);
          
          // If processing is done, get the result and stop polling
          if (statusResponse.status === 'done') {
            const resultResponse = await getJobResult(newJobId);
            setResult(resultResponse.result);
            clearInterval(interval);
            setPollingInterval(null);
          } else if (statusResponse.status === 'error' || statusResponse.status === 'not_found') {
            setError(`Processing failed: ${statusResponse.status}`);
            // Ensure status reflects the error state if polling stops due to error/not_found
            setStatus(statusResponse.status); 
            clearInterval(interval);
            setPollingInterval(null);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Error during status check/result fetch';
          setError(errorMessage);
          // Explicitly set status to 'error' on any polling catch
          setStatus('error'); 
          clearInterval(interval);
          setPollingInterval(null);
        }
      }, 2000); // Poll every 2 seconds
      
      setPollingInterval(interval);
      return { jobId: newJobId, status: uploadResponse.status };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process document');
      setStatus('error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    processDocumentWithFastAPI, 
    status, 
    jobId, 
    result, 
    isLoading, 
    error 
  };
}; 